import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { AuthGuard } from '@/auth/AuthGuard';
import { AdminLayout } from '@/layout/AdminLayout';

/**
 * Route-level code splitting. Each page becomes its own chunk, loaded on
 * demand. The login/magic-link flow no longer drags the full admin bundle
 * into a first-time visitor's initial download, and deeply-nested forms
 * (template editor) only arrive when someone navigates there.
 *
 * Shell components (AuthGuard, AdminLayout, Sidebar, Header, ErrorBoundary)
 * stay eager so the frame renders instantly on every navigation — only the
 * content under <Outlet> suspends while the page chunk loads.
 */

const AccessDeniedPage = lazy(() => import('@/pages/access-denied.page'));
const AiUsagePage = lazy(() => import('@/pages/ai-usage.page'));
const DocumentUnlockSettingsPage = lazy(() => import('@/pages/document-unlock-settings.page'));
const CreditPackageCreatePage = lazy(() => import('@/pages/credit-package-create.page'));
const DocumentsPage = lazy(() => import('@/pages/documents.page'));
const CreditPackageEditPage = lazy(() => import('@/pages/credit-package-edit.page'));
const CreditPackagesPage = lazy(() => import('@/pages/credit-packages.page'));
const CreditSettingsPage = lazy(() => import('@/pages/credit-settings.page'));
const DashboardPage = lazy(() => import('@/pages/dashboard.page'));
const JobQueuePage = lazy(() => import('@/pages/job-queue.page'));
const JobQueueDetailPage = lazy(() => import('@/pages/job-queue-detail.page'));
const LoginPage = lazy(() => import('@/pages/login.page'));
const MagicLinkPage = lazy(() => import('@/pages/magic-link.page'));
const PaymentOrderDetailPage = lazy(() => import('@/pages/payment-order-detail.page'));
const PaymentOrdersPage = lazy(() => import('@/pages/payment-orders.page'));
const TemplateCreatePage = lazy(() => import('@/pages/template-create.page'));
const TemplateEditPage = lazy(() => import('@/pages/template-edit.page'));
const TemplatesPage = lazy(() => import('@/pages/templates.page'));
const UserEditPage = lazy(() => import('@/pages/user-edit.page'));
const UsersPage = lazy(() => import('@/pages/users.page'));
const VoucherCreatePage = lazy(() => import('@/pages/voucher-create.page'));
const VoucherEditPage = lazy(() => import('@/pages/voucher-edit.page'));
const VouchersPage = lazy(() => import('@/pages/vouchers.page'));

function PageFallback() {
    return <div className="flex items-center justify-center p-12 text-muted-foreground">...</div>;
}

export function AppRoutes() {
    return (
        <BrowserRouter>
            <Suspense fallback={<PageFallback />}>
                <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/auth/magic-link" element={<MagicLinkPage />} />
                    <Route path="/access-denied" element={<AccessDeniedPage />} />

                    {/* Protected routes */}
                    <Route element={<AuthGuard />}>
                        <Route element={<AdminLayout />}>
                            <Route index element={<DashboardPage />} />
                            <Route path="users" element={<UsersPage />} />
                            <Route path="users/:id" element={<UserEditPage />} />
                            <Route path="documents" element={<DocumentsPage />} />
                            <Route path="templates" element={<TemplatesPage />} />
                            <Route path="templates/new" element={<TemplateCreatePage />} />
                            <Route path="templates/:id" element={<TemplateEditPage />} />
                            <Route path="credit-packages" element={<CreditPackagesPage />} />
                            <Route path="credit-packages/new" element={<CreditPackageCreatePage />} />
                            <Route path="credit-packages/:id" element={<CreditPackageEditPage />} />
                            <Route path="credit-settings" element={<CreditSettingsPage />} />
                            <Route path="vouchers" element={<VouchersPage />} />
                            <Route path="vouchers/new" element={<VoucherCreatePage />} />
                            <Route path="vouchers/:id" element={<VoucherEditPage />} />
                            <Route path="payment-orders" element={<PaymentOrdersPage />} />
                            <Route path="payment-orders/:id" element={<PaymentOrderDetailPage />} />
                            <Route path="job-queue" element={<JobQueuePage />} />
                            <Route path="job-queue/:id" element={<JobQueueDetailPage />} />
                            <Route path="ai-usage" element={<AiUsagePage />} />
                            <Route path="document-unlock-settings" element={<DocumentUnlockSettingsPage />} />
                        </Route>
                    </Route>
                </Routes>
            </Suspense>
        </BrowserRouter>
    );
}
